import React from 'react';
import { Box, Container, Grid, sxToStyle, Typography } from '../../../../components/ui/LayoutPrimitives';
import {
  ArrowLeft as ArrowBackIcon,
  CreditCard as AccountBalanceWalletRoundedIcon,
  QrCode as QrCode2RoundedIcon,
  ShoppingCart as ShoppingCartCheckoutRounded,
  Trash2 as RemoveShoppingCartIcon,
} from 'lucide-react';
import { TailSpin } from 'react-loading-icons';
import AvikarCartSkeleton from './CartSkeleton';
import { useNavigate } from 'react-router';
import { getDefaultGatewayPaymentMethod } from '../../../payment/paymentMethodUtils';
import RetailDiscountCode from '../../../payment/RetailDiscountCode';

const Button = ({
  children,
  className = '',
  color,
  disabled,
  endIcon,
  fullWidth,
  size,
  startIcon,
  style,
  sx,
  type = 'button',
  variant,
  ...props
}) => (
  <button
    type={type}
    className={`inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${fullWidth ? 'w-full' : ''} ${size === 'large' ? 'min-h-12 px-6 text-base' : ''} ${variant === 'contained' ? 'bg-slate-900 text-white' : 'bg-transparent'} ${variant === 'outlined' ? 'border border-current' : ''} ${color === 'error' ? 'text-red-600' : ''} ${className}`}
    disabled={disabled}
    style={{ ...sxToStyle(sx), ...(style || {}) }}
    {...props}
  >
    {startIcon}
    {children}
    {endIcon}
  </button>
);

const Backdrop = ({ children, open, style, sx }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" style={{ ...sxToStyle(sx), ...(style || {}) }}>
      {children}
    </div>
  );
};

const Divider = ({ className = '', style, sx }) => (
  <hr className={`border-0 border-t border-slate-200 ${className}`} style={{ ...sxToStyle(sx), ...(style || {}) }} />
);

const Paper = ({ children, className = '', elevation, style, sx, variant, ...props }) => (
  <div className={`rounded-lg bg-white ${variant === 'outlined' ? 'border border-slate-200' : ''} ${className}`} style={{ ...sxToStyle(sx), ...(style || {}) }} {...props}>
    {children}
  </div>
);

const Badge = ({ badgeContent, className = '', style, sx }) => (
  <span
    className={`ml-2 inline-flex min-h-6 min-w-6 items-center justify-center rounded-full bg-[#f4a649] px-2 text-xs font-semibold text-white ${className}`}
    style={{ ...sxToStyle(sx), ...(style || {}) }}
  >
    {badgeContent}
  </span>
);

const AvikarCart = ({
  items,
  totalAmount = 0,
  shippingFee = 0,
  tax = 0,
  currencySymbol = '$',
  isLoading = false,
  isProcessing = false,
  onRemoveItem,
  onCheckout,
  onContinueShopping,
  onEmptyCart,
  onQuantityChange,
  selectedPaymentMethod,
  onPaymentMethodChange,
  onCheckoutWithMethod,
  paymentOptions = [],
  discountCode,
  appliedDiscount,
  discountAmount = 0,
  checkoutTotal,
  discountError,
  isApplyingDiscount,
  onDiscountCodeChange,
  onApplyDiscount,
  onRemoveDiscount,
}) => {
  const toSafeNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
  };
  const itemCount = items?.reduce(
    (total, item) => total + toSafeNumber(item.quantity),
    0
  ) || 0;
  const safeTotalAmount = toSafeNumber(totalAmount);
  const safeShippingFee = toSafeNumber(shippingFee);
  const safeTax = toSafeNumber(tax);
  const grandTotal = Number.isFinite(Number(checkoutTotal))
    ? Number(checkoutTotal) + safeShippingFee + safeTax
    : safeTotalAmount + safeShippingFee + safeTax;
  const navigate = useNavigate();
  const fallbackPaymentMethod = getDefaultGatewayPaymentMethod(currencySymbol === '$' ? 'US' : 'IN');
  const currentPaymentMethod = selectedPaymentMethod || fallbackPaymentMethod;
  const availablePaymentOptions = paymentOptions.length > 0 ? paymentOptions : [
    { value: fallbackPaymentMethod, title: fallbackPaymentMethod === 'stripe' ? 'Card Payment' : 'Razorpay', description: fallbackPaymentMethod === 'stripe' ? 'Pay securely online using your card.' : 'Continue with secure online checkout.' },
    { value: 'cash', title: 'Cash', description: 'Create the order first and complete payment offline.' },
    { value: 'zelle', title: 'Zelle', description: 'Create the order first and continue with payment instructions.', requiresReference: true }
  ];
  const selectedPaymentOption = availablePaymentOptions.find((option) => option.value === currentPaymentMethod) || availablePaymentOptions[0] || null;

  const handlePaymentMethodChange = (event) => {
    onPaymentMethodChange?.(event.target.value);
  };

  const handleCheckoutClick = () => {
    if (onCheckoutWithMethod) {
      onCheckoutWithMethod(currentPaymentMethod);
      return;
    }

    onCheckout?.(currentPaymentMethod);
  };

  const getPaymentOptionIcon = (value) => {
    const paymentOption = availablePaymentOptions.find((option) => option.value === value);

    if (paymentOption?.requiresReference) {
      return <QrCode2RoundedIcon className="h-4 w-4" />;
    }

    switch (value) {
      case 'cash':
        return <AccountBalanceWalletRoundedIcon className="h-4 w-4" />;
      default:
        return <ShoppingCartCheckoutRounded className="h-4 w-4" />;
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      py: 8,
      px: { xs: 2, sm: 4 }
    }}>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: 'blur(4px)'
        }}
        open={isLoading || isProcessing}
      >
        <TailSpin stroke="#3498DB" />
      </Backdrop>

      <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 2 } }}>
        <Box sx={{ mb: 6 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            // onClick={() => window.history.back()}
            onClick={() => navigate('/')}

            sx={{
              color: 'text.secondary',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'transparent',
                color: 'primary.main'
              }
            }}
          >
            Continue Shopping
          </Button>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              mt: 3,
              fontWeight: 700,
              color: 'text.primary',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            Your Shopping Cart
            {itemCount > 0 && (
              <Badge
                badgeContent={itemCount}
              />
            )}
          </Typography>
        </Box>

        {items?.length >= 1 ? (
          <Grid container spacing={4}>
            <Grid item xs={12} lg={8}>
              <Paper elevation={0} sx={{
                borderRadius: 2,
                p: 4,
                backgroundColor: 'background.paper',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
              }}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 4
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {itemCount} {itemCount === 1 ? 'Item' : 'Items'} in your cart
                  </Typography>
                  <Button
                    onClick={onEmptyCart}
                    variant="outlined"
                    size="medium"
                    color="error"
                    startIcon={<RemoveShoppingCartIcon />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 500,
                      borderColor: 'error.light',
                      color: 'error.main',
                      '&:hover': {
                        backgroundColor: 'rgba(239, 68, 68, 0.04)',
                        borderColor: 'error.main'
                      }
                    }}
                  >
                    Empty Cart
                  </Button>
                </Box>

                {isLoading ? (
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    py: 8
                  }}>
                    <TailSpin stroke="#3498DB" />
                  </Box>
                ) : (
                  <>
                    {items.map((item, index) => {
                      const productData = item.product || item;
                      const productId = productData?.primaryId || productData?.product?.primaryId;
                      const itemQuantity = Math.max(1, toSafeNumber(item.quantity) || 1);

                      return (
                        <React.Fragment key={index}>
                          <AvikarCartSkeleton
                            item={productData}
                            id={productId}
                            currency={currencySymbol}
                            quantity={itemQuantity}
                            handleRemove={onRemoveItem}
                            setIncrease={() => {
                              onQuantityChange(productId, itemQuantity + 1, itemQuantity);
                            }}
                            setDecrease={() => {
                              onQuantityChange(productId, itemQuantity - 1, itemQuantity);
                            }}
                          />
                          {index < items.length - 1 && (
                            <Divider
                              sx={{
                                my: 3,
                                borderColor: 'divider',
                                opacity: 0.5
                              }}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}

                  </>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Paper elevation={0} sx={{
                borderRadius: 2,
                p: 4,
                backgroundColor: 'background.paper',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                position: 'sticky',
                top: 24
              }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 3,
                    color: 'text.primary'
                  }}
                >
                  Order Summary
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <fieldset className="w-full">
                    <legend
                      className="mb-3 text-sm font-semibold text-slate-950"
                    >
                      Payment Method
                    </legend>
                    <div className="grid gap-2">
                      {availablePaymentOptions.map((option) => {
                        const isSelected = currentPaymentMethod === option.value;
                        return (
                          <label
                            key={option.value}
                            className="flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition"
                            style={{
                              borderColor: isSelected ? '#f4a649' : '#e2e8f0',
                              backgroundColor: isSelected ? 'rgba(244, 166, 73, 0.08)' : '#fff'
                            }}
                          >
                            <input
                              type="radio"
                              name="paymentMethod"
                              value={option.value}
                              checked={isSelected}
                              onChange={handlePaymentMethodChange}
                              className="mt-1 h-4 w-4 accent-[#f4a649]"
                            />
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                              <Box sx={{ color: isSelected ? '#f4a649' : 'text.secondary', mt: 0.15 }}>
                                {getPaymentOptionIcon(option.value)}
                              </Box>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                  {option.title}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                                  {option.description}
                                </Typography>
                              </Box>
                            </Box>
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 1.5
                  }}>
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                      Subtotal ({itemCount} {itemCount === 1 ? 'item' : 'items'})
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {currencySymbol}{safeTotalAmount.toFixed(2)}
                    </Typography>
                  </Box>

                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 1.5
                  }}>
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                      Shipping
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {currencySymbol}{safeShippingFee.toFixed(2)}
                    </Typography>
                  </Box>

                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 2
                  }}>
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                      Tax
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {currencySymbol}{safeTax.toFixed(2)}
                    </Typography>
                  </Box>

                  <RetailDiscountCode
                    appliedDiscount={appliedDiscount}
                    currencySymbol={currencySymbol}
                    discountAmount={discountAmount}
                    discountCode={discountCode}
                    error={discountError}
                    isApplying={isApplyingDiscount}
                    isDisabled={isProcessing}
                    onApply={onApplyDiscount}
                    onChange={onDiscountCodeChange}
                    onRemove={onRemoveDiscount}
                  />

                  {discountAmount > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body1" sx={{ color: '#15803d', fontWeight: 600 }}>
                        Discount
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#15803d', fontWeight: 600 }}>
                        -{currencySymbol}{toSafeNumber(discountAmount).toFixed(2)}
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Total
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {currencySymbol}{grandTotal.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleCheckoutClick}
                  disabled={isProcessing || isApplyingDiscount}
                  endIcon={<ShoppingCartCheckoutRounded />}
                  sx={{
                    backgroundColor: '#f4a649',
                    color: 'white',
                    fontWeight: 600,
                    py: 1.5,
                    mb: 2,
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: '#e6953a',
                    },
                    '&:disabled': {
                      backgroundColor: '#f4a649',
                      opacity: 0.7
                    }
                  }}
                >
                  {isProcessing
                    ? 'Processing...'
                    : currentPaymentMethod === 'cash'
                      ? 'Pay on Cash'
                      : selectedPaymentOption?.requiresReference
                        ? `Pay on ${selectedPaymentOption?.title || selectedPaymentOption?.label || 'Manual Payment'}`
                        : 'Proceed to Checkout'}
                </Button>

                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'center',
                    color: 'text.secondary',
                    mt: 1
                  }}
                >
                  or{' '}
                  <Box
                    component="span"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 500,
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                    onClick={() => navigate('/')}
                  >
                    continue shopping
                  </Box>
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        ) : (
          <Paper elevation={0} sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 10,
            px: 4,
            backgroundColor: 'background.paper',
            borderRadius: 2,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            textAlign: 'center'
          }}>
            <Box sx={{ maxWidth: 400 }}>
              <ShoppingCartCheckoutRounded
                style={{
                  width: 72,
                  height: 72,
                  color: '#cbd5e1',
                  marginBottom: 24
                }}
              />
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                Your cart is empty
              </Typography>
              <Typography variant="body1" sx={{
                color: 'text.secondary',
                mb: 4,
                lineHeight: 1.6
              }}>
                Looks like you haven't added anything to your cart yet. Start shopping to fill it with amazing products!
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={onContinueShopping}
                sx={{
                  backgroundColor: '#f4a649',
                  color: 'white',
                  fontWeight: 600,
                  px: 5,
                  py: 1.5,
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: '#e6953a',
                  }
                }}
              >
                Start Shopping
              </Button>
            </Box>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default AvikarCart;
